import { useState, useEffect } from 'react';

// Shopify Storefront API configuration
const SHOPIFY_CONFIG = {
  domain: 'x16ha3-n1.myshopify.com',
  storefrontAccessToken: 'f1f5374c07806735e6632074755592e5',
};

// Map Shopify product IDs to local IDs and categories
const PRODUCT_CONFIG = {
  'gid://shopify/Product/9150818091262': { localId: '1', category: 'earrings', tagline: "doesn't ask permission." }, // The Cuff Set
  'gid://shopify/Product/9151135908094': { localId: '2', category: 'necklaces', tagline: 'wear it loud.' }, // The Layer
  'gid://shopify/Product/9151135580414': { localId: '3', category: 'rings', tagline: 'stack it all.' }, // The Stack
  'gid://shopify/Product/9151135252734': { localId: '4', category: 'bracelets', tagline: 'gold all day.' }, // The Bangle
  'gid://shopify/Product/9151224283390': { localId: '5', category: 'earrings', tagline: 'drop it low.' }, // The Drop
  'gid://shopify/Product/9151224512766': { localId: '6', category: 'rings', tagline: 'pure liquid gold.' }, // The Melt
  'gid://shopify/Product/9151225069822': { localId: '7', category: 'rings', tagline: 'signed, sealed.' }, // The Signet
  'gid://shopify/Product/9151224709374': { localId: '8', category: 'necklaces', tagline: 'heavy hitter.' }, // The Cuban
  'gid://shopify/Product/9162088808702': { localId: 'lighter', category: 'accessories', tagline: 'gold. heavy. yours.', customUrl: '/the-lighter' }, // The Lighter
  'gid://shopify/Product/9162448929022': { localId: 'watch', category: 'watches', tagline: 'time is an accessory.', customUrl: '/the-watch' }, // The Watch
  'gid://shopify/Product/9175528407294': { localId: 'bag', category: 'bags', tagline: 'four ways to wear it.', customUrl: '/product/bag' }, // The Bag
  // New products - corrected IDs based on Shopify API response
  'gid://shopify/Product/9228395610366': { localId: '12', category: 'earrings', tagline: 'tear drop elegance.' }, // The Tear
  'gid://shopify/Product/9228396626174': { localId: '13', category: 'rings', tagline: 'pour it on.' }, // The Pour
  'gid://shopify/Product/9228400034046': { localId: '14', category: 'rings', tagline: 'chain reaction.' }, // The Chain
  'gid://shopify/Product/9228400754942': { localId: '15', category: 'rings', tagline: 'ride the wave.' }, // The Wave
  'gid://shopify/Product/9228401115390': { localId: '16', category: 'bracelets', tagline: 'draw the line.' }, // The Line
};

const SHOPIFY_PRODUCT_IDS = Object.keys(PRODUCT_CONFIG);

/**
 * Hook to fetch all products directly from Shopify Storefront API
 * Returns products with images, titles, prices - all live from Shopify
 */
export const useShopifyProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      const query = `
        query getProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              description
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              images(first: 10) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(
          `https://${SHOPIFY_CONFIG.domain}/api/2024-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { ids: SHOPIFY_PRODUCT_IDS },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Shopify API error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.errors) {
          console.error('Shopify GraphQL errors:', data.errors);
          throw new Error('Failed to fetch products from Shopify');
        }

        const nodes = data.data?.nodes || [];
        
        // Transform Shopify data to our product format
        const transformedProducts = nodes
          .filter(node => node !== null)
          .map(node => {
            const config = PRODUCT_CONFIG[node.id] || {};
            const images = node.images?.edges?.map(edge => edge.node.url) || [];
            
            // Strip descriptors after dash, use short name only
            let shortName = node.title?.toUpperCase() || 'UNKNOWN';
            if (shortName.includes(' — ')) {
              shortName = shortName.split(' — ')[0].trim();
            } else if (shortName.includes(' - ')) {
              shortName = shortName.split(' - ')[0].trim();
            }
            
            // Get prices from Shopify - sale price and compare-at price
            const salePrice = parseFloat(node.priceRange?.minVariantPrice?.amount || 0);
            const compareAtPrice = parseFloat(node.compareAtPriceRange?.minVariantPrice?.amount || 0);
            
            return {
              id: config.localId || node.id,
              shopifyId: node.id,
              name: shortName,
              description: node.description || '',
              price: salePrice,
              compareAtPrice: compareAtPrice > salePrice ? compareAtPrice : null,
              image: images[0] || '',
              images: images,
              category: config.category || 'other',
              tagline: config.tagline || '',
              customUrl: config.customUrl || null,
              in_stock: true,
            };
          })
          .sort((a, b) => {
            // Sort numeric IDs first, then string IDs
            const aNum = parseInt(a.id);
            const bNum = parseInt(b.id);
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
            if (!isNaN(aNum)) return -1;
            if (!isNaN(bNum)) return 1;
            return String(a.id).localeCompare(String(b.id));
          });

        setProducts(transformedProducts);
      } catch (err) {
        console.error('Error fetching Shopify products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return { products, loading, error };
};

/**
 * Hook to fetch a single product from Shopify by local ID
 */
export const useShopifyProduct = (localId) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      // Find Shopify ID from local ID
      const shopifyId = Object.entries(PRODUCT_CONFIG)
        .find(([_, config]) => config.localId === localId)?.[0];

      if (!shopifyId) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      const query = `
        query getProduct($id: ID!) {
          node(id: $id) {
            ... on Product {
              id
              title
              description
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              images(first: 10) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(
          `https://${SHOPIFY_CONFIG.domain}/api/2024-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { id: shopifyId },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Shopify API error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.errors) {
          console.error('Shopify GraphQL errors:', data.errors);
          throw new Error('Failed to fetch product from Shopify');
        }

        const node = data.data?.node;
        if (!node) {
          throw new Error('Product not found');
        }

        const config = PRODUCT_CONFIG[node.id] || {};
        const images = node.images?.edges?.map(edge => edge.node.url) || [];
        
        // Get prices from Shopify - sale price and compare-at price
        const salePrice = parseFloat(node.priceRange?.minVariantPrice?.amount || 0);
        const compareAtPrice = parseFloat(node.compareAtPriceRange?.minVariantPrice?.amount || 0);
        
        setProduct({
          id: config.localId || node.id,
          shopifyId: node.id,
          name: node.title?.toUpperCase() || 'UNKNOWN',
          description: node.description || '',
          price: salePrice,
          compareAtPrice: compareAtPrice > salePrice ? compareAtPrice : null,
          image: images[0] || '',
          images: images,
          category: config.category || 'other',
          tagline: config.tagline || '',
          in_stock: true,
        });
      } catch (err) {
        console.error('Error fetching Shopify product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (localId) {
      fetchProduct();
    }
  }, [localId]);

  return { product, loading, error };
};

export default useShopifyProducts;
